import React from 'react';

const FEATURE_STYLE_TAG_PATTERN = /(<\/?(?:strong|underline)>)/gi;
const FEATURE_STYLE_TAG = /^<(\/)?(strong|underline)>$/i;

type FeatureStyle = 'strong' | 'underline';

interface ActiveStyles {
  strong: number;
  underline: number;
}

interface StyleTagPositions {
  strong: number[];
  underline: number[];
}

interface FeatureTextSegment {
  bold: boolean;
  position: number;
  underline: boolean;
  value: string;
}

interface PlanFeatureTextProps {
  children: string;
}

/** Identifies style tags that have a matching counterpart of the same type. */
const findPairedStyleTagPositions = (parts: string[]): Set<number> => {
  const openTagPositions: StyleTagPositions = {
    strong: [],
    underline: [],
  };
  const pairedTagPositions = new Set<number>();

  parts.forEach((part, position) => {
    const tagMatch = FEATURE_STYLE_TAG.exec(part);
    if (!tagMatch) return;

    const closing = tagMatch[1] === '/';
    const style: FeatureStyle = tagMatch[2].toLowerCase() === 'strong'
      ? 'strong'
      : 'underline';

    if (!closing) {
      openTagPositions[style].push(position);
      return;
    }

    const openingPosition = openTagPositions[style].pop();
    if (openingPosition === undefined) return;

    pairedTagPositions.add(openingPosition);
    pairedTagPositions.add(position);
  });

  return pairedTagPositions;
};

/**
 * Splits allowlisted strong and underline tags into safely renderable segments.
 * Emoji, unknown tags, and unmatched style tags render exactly as authored.
 */
const parseFeatureText = (featureText: string): FeatureTextSegment[] => {
  const activeStyles: ActiveStyles = { strong: 0, underline: 0 };
  const segments: FeatureTextSegment[] = [];
  const parts = featureText.trim().split(FEATURE_STYLE_TAG_PATTERN).filter(Boolean);
  const pairedTagPositions = findPairedStyleTagPositions(parts);

  parts.forEach(
    (part, position) => {
      const tagMatch = FEATURE_STYLE_TAG.exec(part);

      if (!tagMatch || !pairedTagPositions.has(position)) {
        segments.push({
          bold: activeStyles.strong > 0,
          position,
          underline: activeStyles.underline > 0,
          value: part,
        });
        return;
      }

      const closing = tagMatch[1] === '/';
      const style: FeatureStyle = tagMatch[2].toLowerCase() === 'strong'
        ? 'strong'
        : 'underline';

      activeStyles[style] += closing ? -1 : 1;
    },
  );

  return segments;
};

const renderFeatureSegment = (segment: FeatureTextSegment): React.ReactElement => {
  let content: React.ReactNode = segment.value;

  if (segment.underline) content = <u>{content}</u>;
  if (segment.bold) content = <strong>{content}</strong>;

  return <React.Fragment key={segment.position}>{content}</React.Fragment>;
};

/** Renders the limited formatting supported inside activation plan features. */
const PlanFeatureText: React.FC<PlanFeatureTextProps> = ({ children }) => (
  <>{parseFeatureText(children).map(renderFeatureSegment)}</>
);

export { PlanFeatureText };
